/**
 * useSmartAccount Hook
 * Manages ERC-4337 smart account state for connected guest wallets.
 * Automatically creates/retrieves smart accounts when a wallet connects.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAppKitAccount } from '@reown/appkit/react';
import { aaApi } from '../lib/aaApi';

export function useSmartAccount() {
  const { address, isConnected } = useAppKitAccount();
  const [smartAccount, setSmartAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initSmartAccount = useCallback(async (eoaAddress, email = null) => {
    setLoading(true);
    setError(null);
    try {
      const account = await aaApi.createSmartAccount(eoaAddress, email);
      setSmartAccount(account);
      return account;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAccount = useCallback(async () => {
    if (!address) return;
    setLoading(true);
    try {
      const account = await aaApi.getSmartAccount(address);
      setSmartAccount(account);
    } catch {
      // Account might not exist yet - that's ok
      setSmartAccount(null);
    } finally {
      setLoading(false);
    }
  }, [address]);

  // Auto-fetch smart account when wallet connects
  useEffect(() => {
    if (isConnected && address) {
      refreshAccount();
    } else {
      setSmartAccount(null);
    }
  }, [isConnected, address, refreshAccount]);

  return {
    smartAccount,
    smartAccountAddress: smartAccount?.smart_account_address || null,
    isDeployed: smartAccount?.is_deployed || false,
    loading,
    error,
    initSmartAccount,
    refreshAccount,
    eoaAddress: address,
    isConnected,
  };
}
